/**
 * Formateurs du monitoring, localisés selon la langue de l'interface.
 *
 * Règle du module : le front FORMATE, il ne calcule pas. Ces fonctions ne font
 * que présenter une valeur reçue du serveur (nombre, chaîne décimale, date) ;
 * aucune ne somme, ne divise ni ne déduit de métrique.
 *
 * Les montants arrivent en chaînes décimales exactes. `Number(...)` n'est
 * appliqué qu'au moment de l'affichage, avec exactement l'échelle reçue : la
 * valeur affichée est celle du serveur, jamais un résultat d'arrondi du front.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { LangCode } from '../../../api/monitoring/v2';

const LANGS: readonly LangCode[] = ['fr', 'en', 'ar'];

/**
 * Langue de l'interface réduite aux trois langues servies (`fr-FR` → `fr`).
 * i18next ne résout jamais vers une autre langue que ses `supportedLngs` ; si
 * cela arrivait, c'est son `fallbackLng` (fr) qui serait affiché à l'écran, et
 * c'est donc lui qui doit piloter les formats.
 */
export const toLangCode = (language: string | undefined): LangCode => {
    const base = (language ?? '').toLowerCase().replace('_', '-').split('-')[0];
    return (LANGS as readonly string[]).includes(base) ? (base as LangCode) : 'fr';
};

/**
 * Locale `Intl` de chaque langue. Arabe → `ar-MA` : chiffres latins et
 * conventions marocaines (produit marocain), là où `ar` seul afficherait des
 * chiffres arabes orientaux. Anglais → `en-GB` : jour/mois/année, comme les
 * deux autres langues.
 */
export const INTL_LOCALE: Record<LangCode, string> = {
    fr: 'fr-FR',
    en: 'en-GB',
    ar: 'ar-MA',
};

export const intlLocale = (lang: LangCode): string => INTL_LOCALE[lang];

const DASH = '—';

/** Nombre de décimales porté par une chaîne décimale (`"150.00"` → 2). */
export const decimalsOf = (amount: string): number => {
    const dot = amount.indexOf('.');
    return dot < 0 ? 0 : amount.length - dot - 1;
};

/** Chaîne décimale sans les zéros de fin, au moins `min` décimales. */
export const trimDecimals = (amount: string, min: number): number => {
    const dot = amount.indexOf('.');
    if (dot < 0) return min;
    const significant = amount.slice(dot + 1).replace(/0+$/, '').length;
    return Math.max(min, significant);
};

export const formatCount = (lang: LangCode, value: number | null | undefined): string =>
    value === null || value === undefined || Number.isNaN(value)
        ? DASH
        : new Intl.NumberFormat(intlLocale(lang), { maximumFractionDigits: 0 }).format(value);

/** Chaîne décimale affichée avec exactement `digits` décimales. */
export const formatDecimal = (lang: LangCode, amount: string, digits: number): string =>
    new Intl.NumberFormat(intlLocale(lang), {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }).format(Number(amount));

/**
 * Montant avec devise. La devise est OBLIGATOIRE : aucun défaut, jamais.
 * L'échelle affichée est celle de la chaîne reçue (celle de la devise).
 */
export const formatMoney = (lang: LangCode, amount: string, currency: string, digits = decimalsOf(amount)): string => {
    try {
        return new Intl.NumberFormat(intlLocale(lang), {
            style: 'currency',
            currency,
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
        }).format(Number(amount));
    } catch (err) {
        // Code devise inconnu d'Intl : on montre la valeur brute ET le code,
        // jamais une autre devise.
        // eslint-disable-next-line no-console
        console.warn('[monitoring] devise non reconnue par Intl', currency, err);
        return `${amount} ${currency}`;
    }
};

/**
 * Seuil du format compact : en dessous de 1 000, un comptage s'affiche entier
 * et un montant avec 2 décimales ; à partir de 1 000, une décimale et le
 * suffixe de la langue (5 582 188 → « 5,6 M », 84 783 MAD → « 84,8 k MAD »).
 * Décision du dirigeant du 17/10 : sur l'écran IA, « 5 582 188 » jetons ne se
 * lisaient pas d'un coup d'œil.
 */
export const COMPACT_FROM = 1000;

/** Valeur compacte et valeur exacte (infobulle), `null` si rien n'est abrégé. */
export type CompactText = { text: string; exact: string | null };

/**
 * Format compact UNIQUE du monitoring, pour les comptages (appels, jetons…)
 * et les montants (avec `currency`). L'arrondi est celui d'`Intl` (au plus
 * proche) ; la valeur exacte reçue du serveur est rendue à côté, pour
 * l'infobulle, dès que le texte affiché la perd.
 */
export const formatCompact = (
    lang: LangCode,
    value: number | string | null | undefined,
    currency?: string
): CompactText => {
    if (value === null || value === undefined || value === '') return { text: DASH, exact: null };
    const n = Number(value);
    if (Number.isNaN(n)) return { text: DASH, exact: null };
    const raw = String(value);
    const exact = currency ? formatMoney(lang, raw, currency, trimDecimals(raw, 2)) : formatCount(lang, n);
    let text: string;
    if (Math.abs(n) < COMPACT_FROM) {
        text = currency ? formatMoney(lang, raw, currency, 2) : exact;
    } else {
        const options: Intl.NumberFormatOptions = { notation: 'compact', maximumFractionDigits: 1 };
        try {
            text = new Intl.NumberFormat(intlLocale(lang), currency ? { ...options, style: 'currency', currency } : options).format(n);
        } catch (err) {
            // Code devise inconnu d'Intl : nombre compact suivi du code brut,
            // jamais une autre devise (même règle que formatMoney).
            // eslint-disable-next-line no-console
            console.warn('[monitoring] devise non reconnue par Intl', currency, err);
            text = `${new Intl.NumberFormat(intlLocale(lang), options).format(n)} ${currency}`;
        }
    }
    return { text, exact: text === exact ? null : exact };
};

/**
 * Date locale `YYYY-MM-DD` (ou mois `YYYY-MM`) du serveur, en objet `Date`
 * pour l'axe des graphiques. Construite en date calendaire (minuit local du
 * poste, relu dans le même fuseau par `formatAxisDate`) : le jour affiché est
 * toujours celui de la chaîne, jamais décalé.
 */
export const parseLocalDate = (value: string): Date => {
    const [y, m, d] = value.split('-').map((part) => Number(part));
    return new Date(y, (m || 1) - 1, d || 1);
};

/**
 * Heure murale d'un horodatage du serveur, lue TELLE QUELLE dans la chaîne
 * (contrat v2.2, §11) : `2026-10-11T23:30:00+00:00` → 11/10/2026 23:30.
 *
 * Le serveur envoie l'heure locale du fuseau métier et son décalage ; le
 * décalage est lu (la chaîne doit être bien formée) mais n'est JAMAIS
 * appliqué : aucun instant n'est reconstruit puis reconverti, ni dans le
 * fuseau du navigateur ni via `Intl`. Un navigateur dont la base de fuseaux
 * ignore le passage du Maroc à +00 permanent (2026-09-20) affiche donc la même
 * heure que le serveur et que l'export CSV.
 */
export type WallClock = {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    /** Décalage tel qu'écrit par le serveur (`+00:00`, `+01:00`, ou `Z`). */
    offset: string;
};

const DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export const parseWallClock = (value: string): WallClock | null => {
    const m = DATETIME_RE.exec(value);
    if (!m) return null;
    return {
        year: Number(m[1]),
        month: Number(m[2]),
        day: Number(m[3]),
        hour: Number(m[4]),
        minute: Number(m[5]),
        offset: m[6],
    };
};

type Field = 'year' | 'month' | 'day' | 'hour' | 'minute';
type PatternPart = { field: Field } | { literal: string };

/**
 * Motif d'affichage d'une langue (ordre des champs et séparateurs), tiré
 * d'`Intl.DateTimeFormat#formatToParts` sur une date de référence dont les
 * VALEURS sont jetées : seuls l'ordre et les littéraux servent. Le fuseau
 * n'intervient donc pas dans le résultat.
 */
const PATTERNS = new Map<string, PatternPart[]>();
const REFERENCE = new Date(Date.UTC(2000, 0, 1, 12, 0));

const patternOf = (lang: LangCode, withTime: boolean): PatternPart[] => {
    const key = `${lang}|${withTime}`;
    const cached = PATTERNS.get(key);
    if (cached) return cached;
    const options: Intl.DateTimeFormatOptions = withTime
        ? { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }
        : { day: '2-digit', month: '2-digit', year: 'numeric' };
    const parts = new Intl.DateTimeFormat(intlLocale(lang), options)
        .formatToParts(REFERENCE)
        .flatMap((part): PatternPart[] => {
            if (part.type === 'literal') return [{ literal: part.value }];
            if (part.type === 'year' || part.type === 'month' || part.type === 'day' || part.type === 'hour' || part.type === 'minute') {
                return [{ field: part.type }];
            }
            // hourCycle h23 : ni AM/PM ni autre champ ne peut apparaître.
            throw new Error(`[monitoring] champ de date inattendu pour ${lang} : ${part.type}`);
        });
    PATTERNS.set(key, parts);
    return parts;
};

const DIGITS = new Map<LangCode, { two: Intl.NumberFormat; year: Intl.NumberFormat }>();

const digitsOf = (lang: LangCode) => {
    let digits = DIGITS.get(lang);
    if (!digits) {
        digits = {
            two: new Intl.NumberFormat(intlLocale(lang), { minimumIntegerDigits: 2, useGrouping: false }),
            year: new Intl.NumberFormat(intlLocale(lang), { useGrouping: false }),
        };
        DIGITS.set(lang, digits);
    }
    return digits;
};

/** Champs mis en forme avec les chiffres de la langue (`2` → `02`). */
const renderPattern = (lang: LangCode, pattern: PatternPart[], values: Record<Field, number>): string => {
    const { two, year } = digitsOf(lang);
    return pattern
        .map((part) => ('literal' in part ? part.literal : (part.field === 'year' ? year : two).format(values[part.field])))
        .join('');
};

/** Date calendaire `YYYY-MM-DD` : jamais convertie, jamais lue comme un instant. */
export const formatDate = (lang: LangCode, value: string | null | undefined): string => {
    if (!value) return DASH;
    const m = DATE_RE.exec(value);
    if (!m) return value;
    return renderPattern(lang, patternOf(lang, false), {
        year: Number(m[1]),
        month: Number(m[2]),
        day: Number(m[3]),
        hour: 0,
        minute: 0,
    });
};

/** Horodatage du serveur affiché à l'heure murale qu'il porte (voir `parseWallClock`). */
export const formatDateTime = (lang: LangCode, value: string | null | undefined): string => {
    if (!value) return DASH;
    const wall = parseWallClock(value);
    if (!wall) return value;
    return renderPattern(lang, patternOf(lang, true), wall);
};

/** Graduation d'axe temporel selon le pas de la série. */
export const formatAxisDate = (lang: LangCode, date: Date, grain: 'day' | 'week' | 'month'): string =>
    new Intl.DateTimeFormat(
        intlLocale(lang),
        grain === 'month' ? { month: 'short', year: 'numeric' } : { day: '2-digit', month: 'short' }
    ).format(date);

/** Unité de durée, sans conversion (minutes restent des minutes). */
export const formatMinutes = (lang: LangCode, minutes: number | null | undefined): string =>
    minutes === null || minutes === undefined
        ? DASH
        : new Intl.NumberFormat(intlLocale(lang), { style: 'unit', unit: 'minute', unitDisplay: 'short' }).format(minutes);

export const formatDurationMs = (lang: LangCode, ms: number | null | undefined): string =>
    ms === null || ms === undefined
        ? DASH
        : new Intl.NumberFormat(intlLocale(lang), { style: 'unit', unit: 'millisecond', unitDisplay: 'short' }).format(ms);

/** Formateurs liés à la langue courante (re-calculés quand elle change). */
export const useFormat = () => {
    const { i18n } = useTranslation();
    const lang = toLangCode(i18n.resolvedLanguage ?? i18n.language);
    return useMemo(
        () => ({
            lang,
            count: (v: number | null | undefined) => formatCount(lang, v),
            compact: (v: number | string | null | undefined, currency?: string) => formatCompact(lang, v, currency),
            decimal: (amount: string, digits: number) => formatDecimal(lang, amount, digits),
            money: (amount: string, currency: string, digits?: number) => formatMoney(lang, amount, currency, digits),
            date: (v: string | null | undefined) => formatDate(lang, v),
            dateTime: (v: string | null | undefined) => formatDateTime(lang, v),
            minutes: (v: number | null | undefined) => formatMinutes(lang, v),
            durationMs: (v: number | null | undefined) => formatDurationMs(lang, v),
        }),
        [lang]
    );
};
